"use client"

import clsx from "clsx"
import React, { useEffect, useRef, useState } from "react"
import { BaseInputProps } from "../../utils"
import { TIcon } from "../display/TIcon"
import { TLoader } from "../display/TLoader"
import { TTextInput } from "../input/TTextInput"
import { TMenuItem } from "../menu/TMenuItem"
import { TSelectItem } from "./types"

/**
 * 자동완성 검색 입력
 *
 * @description
 * 비동기 옵션 로딩과 필터링을 지원하는 자동완성 입력 필드입니다.
 * loadOptions로 옵션을 비동기 로드하고, 입력값에 따라 자동 필터링됩니다.
 * freeSolo 모드에서는 목록에 없는 자유 텍스트 입력도 가능합니다.
 *
 * @template V - 값 타입 (string 또는 number)
 *
 * @example
 * ```tsx
 * <TAutocomplete
 *   value={selectedUser}
 *   loadOptions={async () => {
 *     const users = await fetchUsers()
 *     return users.map(u => ({ value: u.id, label: u.name }))
 *   }}
 *   onChange={(value, item) => setSelectedUser(value)}
 *   placeholder="사용자 검색"
 * />
 * ```
 */
interface TAutocompleteProps<V extends string | number> extends BaseInputProps {
	/** 현재 선택된 값 */
	value?: V | null
	/**
	 * 입력 필드 크기
	 * @default "medium"
	 * @ai-style "small" → 높이 32px
	 * @ai-style "medium" → 높이 40px
	 * @ai-style "large" → 높이 48px
	 */
	size?: "small" | "medium" | "large"
	/** 입력 앞 아이콘/텍스트 */
	prefix?: React.ReactNode
	/** 입력 뒤 아이콘/텍스트 */
	suffix?: React.ReactNode
	/** 검색 결과 없을 때 표시 텍스트 @default "No search results found." */
	noOptionsText?: React.ReactNode
	/** 로딩 중 표시 텍스트 @default "Loading..." */
	loadingText?: string
	/** 클리어 버튼 숨김 @default false */
	hiddenClear?: boolean
	/** 자유 텍스트 입력 모드 (목록에 없는 값도 허용) @default false */
	freeSolo?: boolean
	/** 자동 필터링 비활성화 (서버 사이드 필터링 시) @default false */
	disableFiltering?: boolean
	/** 포커스 시 입력 텍스트 전체 선택 @default false */
	selectOnFocus?: boolean
	/** 옵션 비동기 로딩 함수. { value, label } 배열을 반환하는 Promise */
	loadOptions: () => Promise<Array<TSelectItem<V>>>
	/** 값 변경 콜백. value=null이면 선택 해제 */
	onChange?: (value: V | null, item?: TSelectItem<V> | null) => void
}

export const TAutocomplete = React.memo(<V extends string | number>(props: TAutocompleteProps<V>) => {
	const {
		id,
		name,
		className,
		size = "medium",
		placeholder,
		prefix,
		suffix,
		noOptionsText = "No search results found.",
		loadingText = "Loading...",
		disabled,
		readOnly,
		required,
		error,
		autoFocus,
		hiddenClear,
		freeSolo = false,
		disableFiltering = false,
		selectOnFocus = false,
		dataAttributes,
		value,
		loadOptions,
		onChange,
		...restProps
	} = props

	const [open, setOpen] = useState(false)
	const [options, setOptions] = useState<Array<TSelectItem<V>>>([])
	const [loading, setLoading] = useState(false)
	const [inputValue, setInputValue] = useState("")
	const containerRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const menuRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (value !== null && value !== undefined) {
			const selectedItem = options.find((opt) => opt.value === value)
			if (selectedItem) {
				const label = selectedItem.label
				setInputValue(typeof label === "string" ? label : String(label))
			}
		} else {
			setInputValue("")
		}
	}, [value, options])

	useEffect(() => {
		let active = true

		if (!loading) {
			return
		}

		const fetchOptions = async () => {
			try {
				const data = await loadOptions()

				if (active) {
					setOptions(data)
					setLoading(false)
				}
			} catch (error) {
				console.error("Error loading options:", error)
				if (active) {
					setLoading(false)
					setOptions([])
				}
			}
		}

		fetchOptions()

		return () => {
			active = false
		}
	}, [loading, loadOptions])

	const handleClickOutsideRef = React.useRef<(event: MouseEvent) => void>(() => {
		return
	})

	handleClickOutsideRef.current = React.useCallback(
		(event: MouseEvent) => {
			const isOutsideContainer = containerRef.current && !containerRef.current.contains(event.target as Node)
			const isOutsideMenu = menuRef.current && !menuRef.current.contains(event.target as Node)

			if (isOutsideContainer && isOutsideMenu) {
				setOpen(false)
				if (value !== null && value !== undefined) {
					const selectedItem = options.find((opt) => opt.value === value)
					if (selectedItem) {
						const label = selectedItem.label
						setInputValue(typeof label === "string" ? label : String(label))
					}
				} else {
					setInputValue("")
				}
			}
		},
		[value, options]
	)

	useEffect(() => {
		const handler = (e: MouseEvent) => handleClickOutsideRef.current?.(e)
		if (open) {
			document.addEventListener("mousedown", handler)
			return () => {
				document.removeEventListener("mousedown", handler)
			}
		}
	}, [open])

	const handleInputChange = React.useCallback(
		(newValue: string) => {
			setInputValue(newValue)

			// freeSolo 모드일 때 사용자 입력값을 V 타입으로 변환하여 onChange 호출
			if (freeSolo && onChange) {
				if (newValue.trim() === "") {
					onChange(null, null)
				} else {
					// 타입 단언: freeSolo 모드에서는 V가 string 타입이어야 함
					// 주의: freeSolo={true}일 경우 V를 string으로 사용해야 합니다
					onChange(newValue as V, null)
				}
			}

			if (!open && !disabled && !readOnly) {
				setOpen(true)
				if (options.length === 0) {
					setLoading(true)
				}
			}
		},
		[freeSolo, onChange, open, disabled, readOnly, options.length]
	)

	const handleInputFocus = React.useCallback(() => {
		if (!disabled && !readOnly) {
			setOpen(true)
			if (options.length === 0) {
				setLoading(true)
			}
			if (selectOnFocus) {
				inputRef.current?.select()
			}
		}
	}, [disabled, readOnly, options.length, selectOnFocus])

	const handleOptionClick = React.useCallback(
		(option: TSelectItem<V>) => {
			const label = option.label
			setInputValue(typeof label === "string" ? label : String(label))
			onChange?.(option.value, option)
			setOpen(false)
			inputRef.current?.focus()
		},
		[onChange]
	)

	const handleClear = React.useCallback(() => {
		setInputValue("")
		onChange?.(null, null)
		inputRef.current?.focus()
	}, [onChange])

	const handleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			inputRef.current?.blur()
			setOpen(false)
		}
	}, [])

	const filteredOptions = React.useMemo(
		() =>
			disableFiltering
				? options
				: options.filter((option) => {
						const label = option.label
						const labelStr = typeof label === "string" ? label : String(label)
						return labelStr.toLowerCase().includes(inputValue.toLowerCase())
					}),
		[disableFiltering, options, inputValue]
	)

	// freeSolo 모드에서 입력값이 리스트에 없을 때 리스트를 숨기기 위한 조건
	// disableFiltering이 활성화된 경우 항상 옵션을 표시
	const shouldShowOptions = disableFiltering || !freeSolo || filteredOptions.length > 0 || inputValue.trim() === ""

	return (
		<div className="TAutocomplete" ref={containerRef} {...restProps}>
			<TTextInput
				ref={inputRef}
				id={id}
				name={name}
				className={className}
				value={inputValue}
				size={size}
				placeholder={loading ? loadingText : placeholder}
				disabled={disabled}
				readOnly={readOnly}
				required={required}
				error={error}
				autoFocus={autoFocus}
				prefix={prefix}
				suffix={
					suffix ? suffix : loading ? <TLoader size={16} /> : <TIcon name="dropdownDown" className={clsx("transition-transform", { "rotate-180": open })} size={16} />
				}
				hiddenClear={hiddenClear}
				onChange={handleInputChange}
				onFocus={handleInputFocus}
				onClear={handleClear}
				onKeyDown={handleKeyDown}
				dataAttributes={dataAttributes}
			/>

			{open && shouldShowOptions && (
				<div ref={menuRef} className="option-list" role="listbox" aria-live="polite" aria-label="Search results">
					{loading ? (
						<TMenuItem loading label={loadingText} />
					) : filteredOptions.length === 0 ? (
						<TMenuItem disabled label={noOptionsText} />
					) : (
						<>
							{filteredOptions.map((option, index) => {
								const isSelected = value !== null && value !== undefined && option.value === value

								return (
									<div key={index} onMouseDown={(e) => e.preventDefault()}>
										<TMenuItem label={option.label} selected={isSelected ? true : false} onClick={() => handleOptionClick(option)} />
									</div>
								)
							})}
						</>
					)}
				</div>
			)}
		</div>
	)
})

TAutocomplete.displayName = "TAutocomplete"
